import type { ManualClPositionHistoryRow } from "@/domains/validators"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn, dollarsPrettified } from "@/domains/library"

/**
 * Chronological Mint / Burn / Collect timeline for a single position. Rows
 * are already sorted newest-first by the store; this view just formats them
 * and renders BaseScan deep links.
 */
export interface PositionHistoryViewProps {
  rows: Array<ManualClPositionHistoryRow>
  isLoading: boolean
  error: string | undefined
  token0Symbol: string
  token1Symbol: string
  onReload?: () => void
  className?: string
}

export function PositionHistoryView({
  rows,
  isLoading,
  error,
  token0Symbol,
  token1Symbol,
  onReload,
  className,
}: PositionHistoryViewProps) {
  const hasRows = rows.length > 0

  if (error) {
    return (
      <div
        className={cn(
          "rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text",
          className,
        )}
      >
        <div>{error}</div>
        {onReload && (
          <button type="button" onClick={onReload} className="mt-2 underline">
            Retry history
          </button>
        )}
      </div>
    )
  }

  if (!hasRows && !isLoading) {
    return (
      <div
        className={cn(
          "rounded-lg border border-p400-light bg-p400 p-6 text-center text-sm text-t200",
          className,
        )}
      >
        <div className="font-medium text-t100">No history events yet.</div>
        <div className="mt-1 text-xs text-t300">
          The indexer returned an empty lifecycle feed for this position.
        </div>
        {onReload && (
          <button type="button" onClick={onReload} className="mt-3 text-xs text-primary-400 hover:underline">
            Check again
          </button>
        )}
      </div>
    )
  }

  return (
    <section
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5",
        className,
      )}
      data-testid="position-history"
    >
      <header className="flex items-center justify-between">
        <BodyXs className="font-medium uppercase tracking-wide text-t300">
          History
        </BodyXs>
        {isLoading && <span className="text-xs text-t300">Loading…</span>}
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-t300">
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Value</th>
              <th className="px-3 py-2 font-medium">{token0Symbol}</th>
              <th className="px-3 py-2 font-medium">{token1Symbol}</th>
              <th className="px-3 py-2 font-medium">Tx</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={historyRowKey(row)}
                className="rounded-md bg-p300 text-t100"
                data-testid="position-history-row"
              >
                <td className="rounded-l-md px-3 py-2 text-xs font-medium text-t100">
                  <span className={cn("rounded-full px-2 py-0.5", toneClass(row.type))}>
                    {row.type}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-t200">
                  {formatRelativeTime(row.timestamp)}
                </td>
                <td className="px-3 py-2 font-medium text-t100">
                  {row.amountUSD !== undefined && Number.isFinite(row.amountUSD)
                    ? dollarsPrettified(row.amountUSD)
                    : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-t200">
                  {formatAmount(row.amount0)}
                </td>
                <td className="px-3 py-2 text-xs text-t200">
                  {formatAmount(row.amount1)}
                </td>
                <td className="rounded-r-md px-3 py-2 text-xs text-t100">
                  {(() => {
                    const hash = resolveTxHash(row.transaction)
                    if (!hash) return "—"
                    return (
                      <a
                        className="text-primary-400 hover:underline"
                        href={`https://basescan.org/tx/${hash}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {shortHash(hash)}
                      </a>
                    )
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function historyRowKey(row: ManualClPositionHistoryRow): string {
  return `${row.type}-${row.id}-${row.logIndex ?? "no-log"}`
}

function toneClass(kind: ManualClPositionHistoryRow["type"]): string {
  switch (kind) {
    case "Mint":
      return "bg-primary-400/20 text-primary-400"
    case "Burn":
      return "bg-error-background/20 text-error-text"
    case "Collect":
      return "bg-p200 text-t100"
  }
}

function formatRelativeTime(unix: number): string {
  const nowSeconds = Math.floor(Date.now() / 1000)
  const delta = Math.max(0, nowSeconds - unix)
  if (delta < 60) return `${delta}s ago`
  if (delta < 3_600) return `${Math.floor(delta / 60)}m ago`
  if (delta < 86_400) return `${Math.floor(delta / 3_600)}h ago`
  return `${Math.floor(delta / 86_400)}d ago`
}

function formatAmount(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  if (value === 0) return "0"
  const abs = Math.abs(value)
  const digits = abs >= 1 ? 4 : 6
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

function shortHash(hash: string): string {
  if (hash.length <= 10) return hash
  return `${hash.slice(0, 6)}…${hash.slice(-4)}`
}

/**
 * Transaction id arrives as the composite `<hash>-<chainId>`. Prefer the
 * explicit `transactionHash` field and fall back to stripping the chain
 * suffix.
 */
function resolveTxHash(transaction: ManualClPositionHistoryRow["transaction"]): string | undefined {
  if (!transaction) return undefined
  if (transaction.transactionHash && transaction.transactionHash.startsWith("0x")) {
    return transaction.transactionHash
  }
  const id = transaction.id ?? ""
  const lastDash = id.lastIndexOf("-")
  if (id.startsWith("0x") && lastDash > 0) return id.slice(0, lastDash)
  return id || undefined
}
